import { useEffect, useRef, type JSX } from 'react';

import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  JizdaListItem,
  JizdySortBy,
  JizdySortDir,
} from '@/api/types/Jizda';
import { TableSkeleton } from '@/components/loading';
import { BrandChip } from '@/components/shell';
import { useAppSelector } from '@/store/hooks';
import { selectIsMine } from '@/store/userSlice';
import { selectAssignedUserName } from '@/store/usersSlice';
import { formatDateCs, formatTimeCs, formatWeekdayCs } from '@/utils/date';

import { CarrierTypeCaption } from './carrierType';
import { formatSpzForDisplay } from './spzFormat';
import { STORNO_ROW_TINT_ALPHA, StornoChip } from './StornoChip';

const INITIAL_SKELETON_ROWS = 8;
const PAGINATION_SKELETON_ROWS = 3;

// Per-column width strategy (CHR-05: the date/time block moves ahead of the
// route and a new narrow derived "DEN" weekday column is inserted at position
// 2). Column order:
// komodita · DEN · datum · čas · nakládka · vykládka · SPZ · dispečer ·
// dopravce · poznámka · zákazník. DEN gets a narrow fixed budget (it only ever
// renders a 2-char abbrev); the width it borrows is taken from the route +
// commodity columns. Object key order MUST match the visual column order so
// `Object.values()` feeds the skeleton/colgroup in the right sequence.
const COLUMN_WIDTHS = {
  commodity: '12%',
  day: '5%',
  loadingDate: '11%',
  time: '6%',
  loadingPlace: '11%',
  unloadingPlace: '11%',
  spz: '9%',
  dispatcher: '13%',
  carrier: '12%',
  note: '12%',
  customer: '15%',
} as const;

export interface OrdersTableProps {
  orders: JizdaListItem[];
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  sortBy: JizdySortBy;
  sortDir: JizdySortDir;
  onSortChange: (sortBy: JizdySortBy) => void;
  onLoadMore: () => void;
  onOpenOrder: (id: string) => void;
  // Master-detail selected-row highlight (panel target).
  selectedOrderId: string | null;
}

// Chip width budget for the resolved dispatcher name (mockup `.col-dispatch`);
// long Czech names ellipsize, full name via Tooltip.
const DISPATCHER_CHIP_MAX_WIDTH = 140;

// Renders the resolved dispatcher name (or unassigned/`muted` chip) wrapped in
// a Tooltip. Own component so each row calls `useAppSelector` once.
const DispatcherCell = ({ sub }: { sub: string | null }): JSX.Element => {
  const { t } = useTranslation('orders');
  const resolvedName = useAppSelector(selectAssignedUserName(sub));
  // My-jizdy highlight: the dispatcher label renders yellow (`mine`) when the
  // row is assigned to the logged-in user — single shared selector, no ad-hoc
  // sub compare.
  const isMine = useAppSelector(selectIsMine(sub));
  if (sub === null) {
    return (
      <BrandChip variant="danger" dot>
        {t('row.dispatcherUnassigned')}
      </BrandChip>
    );
  }
  return (
    <Tooltip title={resolvedName} placement="top">
      <span>
        <BrandChip
          variant={isMine ? 'mine' : 'brand'}
          dot
          maxWidth={DISPATCHER_CHIP_MAX_WIDTH}
        >
          {resolvedName}
        </BrandChip>
      </span>
    </Tooltip>
  );
};

export const OrdersTable = ({
  orders,
  hasMore,
  loading,
  loadingMore,
  sortBy,
  sortDir,
  onSortChange,
  onLoadMore,
  onOpenOrder,
  selectedOrderId,
}: OrdersTableProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Virtual-scroll surrogate: IntersectionObserver on a bottom sentinel.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  return (
    // Vapas `.card` (DESIGN.md §6.3) wrapping a flush table + pagination
    // footer. flex:1 column so the table body scrolls while the footer stays
    // pinned at the card bottom.
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3, // 12px (DESIGN.md §4 card radius)
        boxShadow: theme.elevation.shadow1,
        overflow: 'hidden',
      }}
    >
      <TableContainer sx={{ flex: 1 }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: COLUMN_WIDTHS.commodity }} />
            <col style={{ width: COLUMN_WIDTHS.day }} />
            <col style={{ width: COLUMN_WIDTHS.loadingDate }} />
            <col style={{ width: COLUMN_WIDTHS.time }} />
            <col style={{ width: COLUMN_WIDTHS.loadingPlace }} />
            <col style={{ width: COLUMN_WIDTHS.unloadingPlace }} />
            <col style={{ width: COLUMN_WIDTHS.spz }} />
            <col style={{ width: COLUMN_WIDTHS.dispatcher }} />
            <col style={{ width: COLUMN_WIDTHS.carrier }} />
            <col style={{ width: COLUMN_WIDTHS.note }} />
            <col style={{ width: COLUMN_WIDTHS.customer }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>{t('column.commodity')}</TableCell>
              {/* DEN — derived weekday of loadingDate (CHR-05). Not sortable
                  (the Datum header below owns the loadingDate sort). */}
              <TableCell>{t('column.day')}</TableCell>
              <TableCell
                sortDirection={sortBy === 'loadingDate' ? sortDir : false}
                data-testid="orders-sort-loadingDate"
              >
                <TableSortLabel
                  active={sortBy === 'loadingDate'}
                  direction={sortBy === 'loadingDate' ? sortDir : 'asc'}
                  onClick={() => onSortChange('loadingDate')}
                >
                  {t('column.loadingDate')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('column.time')}</TableCell>
              <TableCell>{t('column.loadingPlace')}</TableCell>
              <TableCell>{t('column.unloadingPlace')}</TableCell>
              <TableCell>{t('column.spz')}</TableCell>
              <TableCell>{t('column.dispatcher')}</TableCell>
              <TableCell>{t('column.carrier')}</TableCell>
              <TableCell>{t('column.note')}</TableCell>
              <TableCell>{t('column.customer')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && orders.length === 0 ? (
              <TableSkeleton
                rows={INITIAL_SKELETON_ROWS}
                columnWidths={Object.values(COLUMN_WIDTHS)}
              />
            ) : null}
            {orders.map((order) => {
              const isDeleted = order.deleted;
              const isStorno = order.stornoState === 'STORNO';
              const customerName = order.customer?.name ?? '';
              const commodityName = order.commodity?.name ?? '';
              const isSelected = order.id === selectedOrderId;
              const arrival = formatTimeCs(order.plannedArrival);
              const spz = formatSpzForDisplay(order.vehicle?.spz);
              // DOPRAVCE (zadání §5 / ADR-0038): VAPAS own fleet renders the
              // literal "VAPAS" label (carrier FK is null for VAPAS); a
              // contractor renders its carrier name + the KONTRAKTOR caption.
              const isVapas = order.carrierType === 'VAPAS';
              const carrierName = order.carrier?.name ?? '';
              return (
                <TableRow
                  key={order.id}
                  hover
                  onClick={() => onOpenOrder(order.id)}
                  selected={isSelected}
                  sx={{
                    cursor: 'pointer',
                    opacity: isDeleted ? 0.5 : 1,
                    // STORNO (zadání §6b / ADR-0039): full-row red tint via the
                    // theme `error` palette token (orthogonal to soft-delete —
                    // a storno row can also be deleted, in which case it keeps
                    // the line-through + opacity below). Applied to every `td`
                    // so the whole row reads red even under the sticky table.
                    ...(isStorno
                      ? {
                          '& > td': {
                            bgcolor: alpha(
                              theme.palette.error.main,
                              STORNO_ROW_TINT_ALPHA,
                            ),
                          },
                        }
                      : null),
                    // Selected-row highlight (§8.2): 2px primary left border
                    // + primary.light tint. `box-shadow inset` avoids layout
                    // shift from a real border on a table cell. On a STORNO row
                    // the red tint stays (storno is the louder signal); only
                    // the primary left-border marks the selection.
                    ...(isSelected
                      ? {
                          ...(isStorno
                            ? null
                            : {
                                '& > td': {
                                  bgcolor: theme.palette.primary.light,
                                },
                              }),
                          '& > td:first-of-type': {
                            boxShadow: `inset 2px 0 0 0 ${theme.palette.primary.main}`,
                          },
                        }
                      : null),
                    '& td': isDeleted
                      ? {
                          textDecoration: 'line-through',
                          color: theme.palette.text.disabled,
                        }
                      : undefined,
                    verticalAlign: 'middle',
                  }}
                  data-testid={`order-row-${order.id}`}
                  data-deleted={isDeleted ? 'true' : 'false'}
                  data-selected={isSelected ? 'true' : 'false'}
                  data-storno={order.stornoState === 'STORNO' ? 'true' : 'false'}
                >
                  {/* KOMODITA — first column (CHR-04). Plain body text; the
                      STORNO chip rides here so the (now-leading) column still
                      carries the storno signal next to the selected-row left
                      border. */}
                  <TableCell data-testid={`order-commodity-${order.id}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={0.75}
                      sx={{ minWidth: 0 }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 13,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        {commodityName}
                      </Typography>
                      {isStorno ? <StornoChip /> : null}
                    </Stack>
                  </TableCell>
                  {/* DEN — derived weekday abbrev of loadingDate (CHR-05).
                      Rendered like the date cell (monospace / tabular-nums,
                      secondary text); read-only, not sortable. */}
                  <TableCell
                    sx={{
                      fontFamily: theme.monospaceFontFamily,
                      fontFeatureSettings: '"tnum"',
                      whiteSpace: 'nowrap',
                      color: theme.palette.text.secondary,
                    }}
                    data-testid={`order-day-${order.id}`}
                  >
                    {formatWeekdayCs(order.loadingDate)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: theme.monospaceFontFamily,
                      fontFeatureSettings: '"tnum"',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDateCs(order.loadingDate)}
                  </TableCell>
                  {/* ČAS — planned arrival as bare `HH:mm` (CHR-03: drop the
                      "od" prefix; the column is too wide otherwise). */}
                  <TableCell
                    sx={{
                      fontFamily: theme.monospaceFontFamily,
                      fontFeatureSettings: '"tnum"',
                      whiteSpace: 'nowrap',
                    }}
                    data-testid={`order-time-${order.id}`}
                  >
                    {arrival}
                  </TableCell>
                  <TableCell>{order.loadingPlace ?? ''}</TableCell>
                  <TableCell>{order.unloadingPlace ?? ''}</TableCell>
                  {/* SPZ — display-formatted (space after 3rd char, ADR-0038);
                      canonical store stays whitespace-free */}
                  <TableCell
                    sx={{
                      fontFamily: theme.monospaceFontFamily,
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                    data-testid={`order-spz-${order.id}`}
                  >
                    {spz}
                  </TableCell>
                  <TableCell>
                    <DispatcherCell sub={order.assignedDispatcherSub} />
                  </TableCell>
                  {/* DOPRAVCE — VAPAS literal, else contractor name + caption */}
                  <TableCell data-testid={`order-carrier-${order.id}`}>
                    {isVapas ? (
                      <Typography
                        component="span"
                        sx={{ fontSize: 13, color: theme.palette.text.primary }}
                      >
                        {t('carrierType.VAPAS')}
                      </Typography>
                    ) : carrierName ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          component="span"
                          sx={{ fontSize: 13, color: theme.palette.text.primary }}
                        >
                          {carrierName}
                        </Typography>
                        <CarrierTypeCaption carrierType={order.carrierType} />
                      </Box>
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontSize: 13, color: theme.palette.text.disabled }}
                      >
                        {t('row.carrierUnassigned')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      color: theme.palette.text.secondary,
                      maxWidth: 220,
                    }}
                  >
                    <Box
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {order.noteForDispatcherSnippet ?? ''}
                    </Box>
                  </TableCell>
                  {/* ZÁKAZNÍK — last column (CHR-04). Plain (non-bold) body
                      text; the bold weight of the former leading column is
                      dropped per the change-spec. */}
                  <TableCell data-testid={`order-customer-${order.id}`}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 13,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        minWidth: 0,
                      }}
                    >
                      {customerName}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {loadingMore && orders.length > 0 ? (
              <TableSkeleton
                rows={PAGINATION_SKELETON_ROWS}
                columnWidths={Object.values(COLUMN_WIDTHS)}
              />
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Vapas `.tbl-pagination` footer (DESIGN.md §7). Infinite-scroll model:
          the sentinel doubles as the "Zobrazeno X" line; load-more is driven by
          the IntersectionObserver above, so no page chevrons are wired. */}
      <Box
        ref={sentinelRef}
        sx={{
          p: theme.spacing(1.25, 2),
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          fontSize: 12,
          color: theme.palette.text.secondary,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          minHeight: 36,
        }}
        data-testid="orders-sentinel"
      >
        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {t('pagination.shownSimple', { shown: orders.length })}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {hasMore ? (
          <Typography variant="caption">{t('pagination.loadingMore')}</Typography>
        ) : orders.length === 0 ? (
          <Typography variant="caption">{t('list.empty')}</Typography>
        ) : (
          <Typography variant="caption">{t('list.endOfList')}</Typography>
        )}
      </Box>
    </Box>
  );
};
